/**
 * ComplianceGauge Component - Governance score visualization
 */

import React from 'react'
import { Card } from '../ui'
import { getScoreColor, getScoreLabel } from '../../lib/governanceScore'

const ComplianceGauge = ({
  score = 0,
  label = 'Governance Health Score',
  size = 'md',
  showDetails = true,
  className = '',
  ...props
}) => {
  const hc = getScoreColor(score)
  const labelText = getScoreLabel(score)
  
  const sizes = {
    sm: 'w-16 h-16 text-sm',
    md: 'w-24 h-24 text-lg',
    lg: 'w-32 h-32 text-2xl',
    xl: 'w-40 h-40 text-3xl',
  }

  const strokeWidths = {
    sm: 4,
    md: 6,
    lg: 8,
    xl: 10,
  }

  const strokeWidth = strokeWidths[size] || 6
  const radius = {
    sm: 28,
    md: 40,
    lg: 54,
    xl: 68,
  }[size] || 40

  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  return (
    <Card className={`p-6 ${className}`} {...props}>
      <div className="flex flex-col items-center">
        {/* Gauge */}
        <div className="relative">
          <svg 
            className={`${sizes[size] || sizes.md} -rotate-90`}
            viewBox={`0 0 ${(radius + 10) * 2} ${(radius + 10) * 2}`}
          >
            {/* Background circle */}
            <circle
              cx={radius + 10}
              cy={radius + 10}
              r={radius}
              fill="none"
              stroke="#E5E7EB"
              strokeWidth={strokeWidth}
            />
            {/* Progress circle */}
            <circle
              cx={radius + 10}
              cy={radius + 10}
              r={radius}
              fill="none"
              stroke="#5D2A86"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`font-bold text-ink ${sizes[size]?.replace('w-', '').replace('h-', 'text-') || 'text-lg'}`}>
              {score}%
            </span>
            <span className="text-[10px] font-medium text-muted">{labelText}</span>
          </div>
        </div>

        {/* Label */}
        {label && (
          <p className="mt-3 text-sm font-medium text-ink text-center">
            {label}
          </p>
        )}

        {/* Details */}
        {showDetails && (
          <div className="mt-4 w-full">
            <div className="flex items-center justify-between text-xs text-muted">
              <span>Foundation</span>
              <span>Operational</span>
              <span>Institutional</span>
            </div>
            <div className="mt-1 flex gap-1">
              <div className="h-1.5 flex-1 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-brand rounded-full transition-all duration-500" style={{ width: '85%' }} />
              </div>
              <div className="h-1.5 flex-1 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-brand rounded-full transition-all duration-500" style={{ width: '70%' }} />
              </div>
              <div className="h-1.5 flex-1 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-brand rounded-full transition-all duration-500" style={{ width: '92%' }} />
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

ComplianceGauge.displayName = 'ComplianceGauge'

export default ComplianceGauge